import { type MetaFunction } from '@remix-run/node'
import { Link } from '@remix-run/react'

export const meta: MetaFunction = () => [
	{ title: 'What to look for in an insurance agent' },
	{
		name: 'description',
		content: 'How to choose an insurance agent.',
	},
]

export default function Component() {
	return (
		<>
			<h1 className="py-3 text-4xl font-black">
				What to look for in an insurance agent
			</h1>
			<h2 className="p-4 text-2xl font-semibold">Why an agent is important</h2>
			<p
				data-paragraph
				className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
			>
				Having an agent adds value to your life in many ways. By working with an
				agency, you gain the combined experience of thousands of insurance
				transactions and the expertise to answer your questions and assist you
				through your purchasing decisions, but it doesn't stop there. Once you
				have purchased the insurance policy or plan, the agency can be involved
				with the ongoing service needs and can help you know that your
				interactions with the insurance company are being handled fairly.
				Regular policy reviews with your agent can help stay on top of your
				changing needs.
			</p>
			<h2 className="p-4 text-2xl font-semibold">How to choose an agent</h2>
			<p
				data-paragraph
				className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
			>
				There are a lot of factors that you can use to evaluate an agency.
				Ultimately there are a few key characteristics that you should look at
				when selecting an agent:
			</p>
			<h2 className="p-4 text-2xl font-semibold">ABILITY</h2>
			<p
				data-paragraph
				className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
			>
				<strong>Competency:</strong> Every state has guidelines and a regulatory
				body that governs the insurance industry and grants licenses for agents
				and brokers to do business in that state. The ability to qualify for an
				insurance agent or broker license may suffice for a state insurance
				department, but it is only one step in developing the competency
				required to adequately solve problems. Look for someone who has
				demonstrated their ability through years of experience.
			</p>
			<p
				data-paragraph
				className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
			>
				<strong>Specialization:</strong> It can be helpful if your agent has
				solved similar problems to the ones you are facing. Some agents make it
				their priority to continuously learn and grow so they are better
				positioned to handle an ever-changing industry.
			</p>
			<h2 className="p-4 text-2xl font-semibold"> SHARED VALUES</h2>
			<p
				data-paragraph
				className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
			>
				<strong>Trustworthiness/Integrity</strong> - You need to know that the
				agent will do everything in his or her power to conduct business in an
				ethical manner and to do what is right.
			</p>
			<p
				data-paragraph
				className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
			>
				<strong>Ease of Doing Business:</strong> While it's common to do
				business locally, some agents give a level of service unmatched by
				others and merit a long-distance relationship.
			</p>
			<h2 className="p-4 text-2xl font-semibold">RELATIONSHIP</h2>
			<p
				data-paragraph
				className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
			>
				Many agents have built a legacy in the insurance industry and provide a
				consistently high level of service to their clients. They earn the
				repeat business of their clients through genuine life-long relationships
				that endure the test of time. All insurance companies experience periods
				of competitiveness and times when they are not so competitive. We at the
				Steele Agency have chosen to be an independent insurance agency because
				we appreciate that it gives us the opportunity to build those enduring
				relationships with our clients. Our ability to service clients with a
				variety of insurance carriers allows us to find competitive rates during
				periods of time when rates go up for everyone. As the insurance industry
				continues to evolve and more techniques are developed by insurers to be
				competitively priced for the best policyholders, it is beneficial to be
				able to offer clients multiple options. In such instances, clients can
				have the peace of mind that they are paying a fair premium. Products
				from different insurers are not all created equally.
			</p>
			<p
				data-paragraph
				className="mt-6 text-xl/7 text-muted-foreground xl:mt-8 xl:text-xl/6 xl:leading-10"
			>
				Read next:{' '}
				<Link
					to="/articles/what-to-look-for-in-an-insurance-company/"
					prefetch="intent"
					className="text-blue-600 underline"
				>
					What to look for in an insurance company
				</Link>
			</p>
		</>
	)
}
